// share.ts — "Teilen"-style shareable link (Feature 3): encodes a ScenarioState into a
// compact, URL-safe string carried in the URL HASH as `#share=<encoded>` — a distinct
// format from the app's own route hashes (always `#/<view>`, see router.ts), so a share
// link never gets misread as a navigation target and the router's own parseHash() simply
// falls back to its default view (#cockpit) when it sees one, no special-casing needed
// there. Reuses scenarioIO.ts's serializeScenario/deserializeScenario — a shared link is
// just an export whose bytes live in the URL instead of a downloaded file, so both paths
// share ONE validator.
import { deserializeScenario, serializeScenario } from './scenarioIO.js';
import type { ScenarioImportResult } from './scenarioIO.js';
import type { ScenarioState } from './scenario.js';

const SHARE_HASH_RE = /^#share=(.+)$/;

/** Unicode-safe base64url (RFC 4648 §5: '+'/'/' -> '-'/'_', no padding) — plain btoa()
 * throws on non-Latin1 characters (e.g. a scenario label with German umlauts/emoji),
 * hence the encodeURIComponent/escape round trip. */
function base64UrlEncode(input: string): string {
  const base64 = btoa(unescape(encodeURIComponent(input)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return decodeURIComponent(escape(atob(padded)));
}

export function encodeScenarioToShareString(scenario: ScenarioState): string {
  return base64UrlEncode(serializeScenario(scenario));
}

/** Never throws: an invalid encoding is reported the same way an invalid JSON blob is
 * (deserializeScenario's honest {ok:false, error} shape), never a crash. */
export function decodeScenarioFromShareString(encoded: string): ScenarioImportResult {
  let json: string;
  try {
    json = base64UrlDecode(encoded);
  } catch {
    return { ok: false, error: 'Der Link konnte nicht gelesen werden (ungültige Kodierung).' };
  }
  return deserializeScenario(json);
}

/** Builds a full shareable URL: current origin + path, hash replaced with
 * `#share=<encoded>` (any existing app route hash is dropped — the receiving side always
 * lands on the default view and is offered the decoded scenario via a banner, see
 * app.tsx). */
export function buildShareUrl(scenario: ScenarioState, currentHref: string = location.href): string {
  const url = new URL(currentHref);
  url.hash = `share=${encodeScenarioToShareString(scenario)}`;
  return url.toString();
}

/** Extracts the encoded payload from a `#share=...` hash, or null if `hash` isn't in
 * that format (e.g. a normal `#/cockpit` route hash, or empty). */
export function extractShareHash(hash: string): string | null {
  const m = SHARE_HASH_RE.exec(hash);
  return m ? m[1]! : null;
}

/** Clears a consumed share hash from the address bar without adding a history entry or
 * triggering the router's hashchange listener with a route change — used once the user
 * has picked an action (load into A/B, or dismiss) so a page refresh doesn't re-offer the
 * same link forever. */
export function clearShareHash(): void {
  if (typeof history === 'undefined' || typeof location === 'undefined') return;
  history.replaceState(null, '', location.pathname + location.search);
}
